// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// The dot-matrix display (M3-17, M3-18, 04 §12, 05 §6).
//
// A 128 × 32 grid of four-bit dots, drawn by generator operations rather than by bitmaps. The
// dot-mask shading — round dots with a phosphor falloff behind backglass glass — is the
// renderer's job; this file owns the **raster**, which is the part that has to be right and
// the part that can be tested.
//
// **Four bits a dot, not one.** A real plasma DMD has visible intensity levels, and the
// difference between a 1-bit display and a 4-bit one is most of why the era's animations still
// look good. Sixteen levels also give dissolves and wipes something to interpolate through.
//
// **The scene player is step-driven, never clock-driven.** A scene knows how many simulation
// steps it has been running, not how many milliseconds have elapsed, so a replay re-rendered
// on a different machine shows the same frame at the same moment. That is the same rule as the
// rest of the core, applied to something that only exists to be looked at — because a ghost
// replay is a recording of what the player saw, and "close enough" is not a recording.

import { DISPLAY_OPS } from '@core/machine/types'
import type { DisplayOp, MachineDisplay } from '@core/machine/types'
import { Rng } from '@core/rng/Rng'
import { STEP_HZ } from '@core/sim/constants'

export const DMD_WIDTH = 128
export const DMD_HEIGHT = 32
/** Intensity levels a dot can take. Four bits, as the hardware had. */
export const DMD_LEVELS = 16

/**
 * Dot colours by the palette name a machine's `display.palette` may take (04 §12).
 *
 * Here rather than beside the renderer that first needed it, because there are now two: the
 * backglass panel in the 3-D scene and the phone's DMD strip (08 §5.1). A machine whose glass is
 * amber in one and the shader's default in the other would be one machine drawn two ways, and
 * nothing in the product would have said so.
 */
export const DMD_PALETTES: Readonly<Record<string, readonly [number, number, number]>> = {
  amber: [1.0, 0.63, 0.16],
  red: [1.0, 0.22, 0.16],
  green: [0.36, 1.0, 0.42],
  white: [0.92, 0.96, 1.0],
}

/** The palette a name asks for. Amber where the name is unknown — it is what a DMD *is*. */
export function dmdPalette(name: string | undefined): readonly [number, number, number] {
  return DMD_PALETTES[name ?? 'amber'] ?? DMD_PALETTES['amber']!
}

/**
 * A 128 × 32 raster of 4-bit dots.
 *
 * `Uint8Array` rather than a bit-packed buffer: 4 kB is nothing, and packing would make every
 * op in this file harder to read for no measurable gain.
 */
export class DmdRaster {
  readonly width: number
  readonly height: number
  readonly dots: Uint8Array

  constructor(width = DMD_WIDTH, height = DMD_HEIGHT) {
    this.width = width
    this.height = height
    this.dots = new Uint8Array(width * height)
  }

  clear(level = 0): void {
    this.dots.fill(level)
  }

  set(x: number, y: number, level: number): void {
    if (x < 0 || y < 0 || x >= this.width || y >= this.height) return
    this.dots[y * this.width + x] = level < 0 ? 0 : level > 15 ? 15 : level | 0
  }

  get(x: number, y: number): number {
    if (x < 0 || y < 0 || x >= this.width || y >= this.height) return 0
    return this.dots[y * this.width + x]!
  }

  /** Brightest dot in the raster. Cheap way for a test to say "something was drawn". */
  peak(): number {
    let peak = 0
    for (let i = 0; i < this.dots.length; i++) if (this.dots[i]! > peak) peak = this.dots[i]!
    return peak
  }

  /** Rows of `.:-=+*#@`-style characters. Only ever used by a failing test's output. */
  toText(): string {
    const ramp = ' .,:;-=+*#%@$&W'
    const lines: string[] = []
    for (let y = 0; y < this.height; y++) {
      let line = ''
      for (let x = 0; x < this.width; x++) {
        line += ramp[Math.min(ramp.length - 1, this.get(x, y))]
      }
      lines.push(line)
    }
    return lines.join('\n')
  }
}

// --- a 5 × 7 font, because a DMD has no fonts ------------------------------------------
//
// Drawn as five column bitmasks per glyph, bit 0 at the top. Seven rows is the classic height
// and leaves room for a descender-free uppercase alphabet plus digits, which is all a pinball
// display ever needed.

const GLYPHS: Record<string, readonly number[]> = {
  A: [0x7e, 0x11, 0x11, 0x11, 0x7e], B: [0x7f, 0x49, 0x49, 0x49, 0x36],
  C: [0x3e, 0x41, 0x41, 0x41, 0x22], D: [0x7f, 0x41, 0x41, 0x22, 0x1c],
  E: [0x7f, 0x49, 0x49, 0x49, 0x41], F: [0x7f, 0x09, 0x09, 0x09, 0x01],
  G: [0x3e, 0x41, 0x49, 0x49, 0x7a], H: [0x7f, 0x08, 0x08, 0x08, 0x7f],
  I: [0x00, 0x41, 0x7f, 0x41, 0x00], J: [0x20, 0x40, 0x41, 0x3f, 0x01],
  K: [0x7f, 0x08, 0x14, 0x22, 0x41], L: [0x7f, 0x40, 0x40, 0x40, 0x40],
  M: [0x7f, 0x02, 0x0c, 0x02, 0x7f], N: [0x7f, 0x04, 0x08, 0x10, 0x7f],
  O: [0x3e, 0x41, 0x41, 0x41, 0x3e], P: [0x7f, 0x09, 0x09, 0x09, 0x06],
  Q: [0x3e, 0x41, 0x51, 0x21, 0x5e], R: [0x7f, 0x09, 0x19, 0x29, 0x46],
  S: [0x46, 0x49, 0x49, 0x49, 0x31], T: [0x01, 0x01, 0x7f, 0x01, 0x01],
  U: [0x3f, 0x40, 0x40, 0x40, 0x3f], V: [0x1f, 0x20, 0x40, 0x20, 0x1f],
  W: [0x3f, 0x40, 0x38, 0x40, 0x3f], X: [0x63, 0x14, 0x08, 0x14, 0x63],
  Y: [0x07, 0x08, 0x70, 0x08, 0x07], Z: [0x61, 0x51, 0x49, 0x45, 0x43],
  '0': [0x3e, 0x51, 0x49, 0x45, 0x3e], '1': [0x00, 0x42, 0x7f, 0x40, 0x00],
  '2': [0x42, 0x61, 0x51, 0x49, 0x46], '3': [0x21, 0x41, 0x45, 0x4b, 0x31],
  '4': [0x18, 0x14, 0x12, 0x7f, 0x10], '5': [0x27, 0x45, 0x45, 0x45, 0x39],
  '6': [0x3c, 0x4a, 0x49, 0x49, 0x30], '7': [0x01, 0x71, 0x09, 0x05, 0x03],
  '8': [0x36, 0x49, 0x49, 0x49, 0x36], '9': [0x06, 0x49, 0x49, 0x29, 0x1e],
  ' ': [0x00, 0x00, 0x00, 0x00, 0x00], '-': [0x08, 0x08, 0x08, 0x08, 0x08],
  '.': [0x00, 0x60, 0x60, 0x00, 0x00], ',': [0x00, 0x50, 0x30, 0x00, 0x00],
  ':': [0x00, 0x36, 0x36, 0x00, 0x00], '!': [0x00, 0x00, 0x5f, 0x00, 0x00],
  '?': [0x02, 0x01, 0x51, 0x09, 0x06], '/': [0x20, 0x10, 0x08, 0x04, 0x02],
  '<': [0x08, 0x14, 0x22, 0x41, 0x00], '>': [0x00, 0x41, 0x22, 0x14, 0x08],
  '+': [0x08, 0x08, 0x3e, 0x08, 0x08], '*': [0x14, 0x08, 0x3e, 0x08, 0x14],
  // Quotation marks and brackets, for the sentences the notice panel draws (20 Sep 2026).
  "'": [0x00, 0x00, 0x07, 0x00, 0x00], '"': [0x00, 0x07, 0x00, 0x07, 0x00],
  '(': [0x00, 0x1c, 0x22, 0x41, 0x00], ')': [0x00, 0x41, 0x22, 0x1c, 0x00],
  '&': [0x36, 0x49, 0x56, 0x20, 0x50], '%': [0x62, 0x64, 0x08, 0x13, 0x23],
}

/**
 * The typographic characters this product's sentences are written with, as the face draws them.
 *
 * **Every dash on the notice panel was a question mark** (found 20 Sep 2026, fitting Keep's
 * sentences): `XRRuntime`'s toasts are written with an em dash, curly quotes and an ellipsis, the
 * face has none of them, and an unknown character draws as `?` — *"Not sure where you are yet ?
 * try again in a moment."* on every headset since M9-47, and the native panel the same. The
 * sentences keep their typography, because they are also read on the page and in the console; the
 * face translates on the way in, and measures what it will draw. `SessionNotice.cs` mirrors this
 * table character for character, and the parity fixture's `litDots` is what holds the two together.
 */
const DRAWABLE: Readonly<Record<string, string>> = {
  '\u2014': '-', // em dash
  '\u2013': '-', // en dash
  '\u2012': '-', // figure dash
  '\u2212': '-', // minus sign
  '\u2018': "'", // left single quotation mark
  '\u2019': "'", // right single quotation mark / apostrophe
  '\u201c': '"', // left double quotation mark
  '\u201d': '"', // right double quotation mark
  '\u2026': '...', // horizontal ellipsis
  '\u00a0': ' ', // no-break space
  '\u202f': ' ', // narrow no-break space (French sets one before a colon)
  '\u00d7': 'x', // multiplication sign
  // French on a five-by-seven face (M13-26): the bench's toasts are in the player's language,
  // and a dot-matrix display has always written French in capitals without their accents —
  // *SYMETRIE ACTIVEE*, as every real machine's display does. The lowercase forms are here for
  // the same reason as the capitals: `drawable` translates first and raises case after.
  à: 'a', â: 'a', ä: 'a', ç: 'c', é: 'e', è: 'e', ê: 'e', ë: 'e', î: 'i', ï: 'i', ô: 'o', ö: 'o', ù: 'u', û: 'u', ü: 'u', œ: 'oe',
  À: 'A', Â: 'A', Ä: 'A', Ç: 'C', É: 'E', È: 'E', Ê: 'E', Ë: 'E', Î: 'I', Ï: 'I', Ô: 'O', Ö: 'O', Ù: 'U', Û: 'U', Ü: 'U', Œ: 'OE',
  '«': '"',
  '»': '"',
}

/** The text as the face will draw it: typography translated, case raised. */
export function drawable(text: string): string {
  let out = ''
  for (const raw of text) out += DRAWABLE[raw] ?? raw
  return out.toUpperCase()
}

/** Whether the face has a glyph for every character of `text`, once translated. */
export function canDraw(text: string): boolean {
  for (const character of drawable(text)) if (GLYPHS[character] === undefined) return false
  return true
}

const GLYPH_WIDTH = 5
const GLYPH_ADVANCE = 6
const GLYPH_HEIGHT = 7

/**
 * Steps between phases of the initials cursor. 250 at 1 kHz is a 2 Hz blink.
 *
 * **Step arithmetic, never a clock.** This display is driven by step index, so a wall-clock blink
 * would draw one thing in a live game and another in its replay — and `dmdEntryParity` samples
 * exact steps either side of a blink edge on both clients.
 */
export const BLINK_STEPS = 250

/** What the glass says while it waits. 14 glyphs, 83 px — fits a 128-wide display. */
export const ENTRY_PROMPT = 'ENTER INITIALS'


export function measureText(text: string): number {
  const drawn = drawable(text)
  return drawn.length === 0 ? 0 : drawn.length * GLYPH_ADVANCE - 1
}

/**
 * The smallest run of dark columns two fields on one row may share (M8-67).
 *
 * One glyph advance — five lit columns and their one-column spacer — because that is the gap the eye
 * already reads as "a space" inside a word. Anything narrower reads as one word.
 */
export const FIELD_GAP = GLYPH_ADVANCE

export interface BottomRowLayout {
  readonly ballX: number
  /** -1 when the credits are not drawn. */
  readonly creditsX: number
  /** -1 when there is no mode. */
  readonly modeX: number
}

/**
 * Lay out `BALL n`, `CREDITS n` and the mode name along the bottom row (M8-67).
 *
 * ***The row was over-committed by design, on both clients.*** The ball sat at the left, the
 * credits were centred, the mode name was right-aligned, and the three were laid out without ever
 * being asked whether they fit. On a 128-column display `BALL 2` spans columns 1—35 and a centred
 * `CREDITS 1` starts at 37: **one dark column between them**, which a headset reported as *"no
 * spacing between balls and credit on pinball display panel"*. At ten credits the two overlapped by
 * two columns, and a ten-glyph mode name overlapped the credits by twenty-one. Every case of it had
 * shipped since M8-39 because every test asked whether the text was drawn, not where.
 *
 * **The ball and the mode own the corners; the credits take what is left.** Centred in the span
 * between them when there is room for the text and a `FIELD_GAP` either side. With no mode running
 * that is most of the row. While one runs its name and its draining bar own the bottom, and the
 * credit line — standing information, not an event — steps aside for the seconds it lasts rather than
 * being drawn on top of it: a real machine does the same with its status line during a mode.
 *
 * Pure, and the same arithmetic as `Dmd.BottomRow` in C#, so a test can sweep every credit count
 * against every mode-name length on both clients and assert the gaps rather than eyeball them.
 */
export function bottomRow(width: number, ball: string, credits: string, mode: string): BottomRowLayout {
  const ballX = 1
  const ballEnd = ballX + measureText(ball)
  const modeX = mode.length === 0 ? -1 : width - measureText(mode) - 1
  const spanStart = ballEnd + FIELD_GAP
  const spanEnd = (modeX < 0 ? width - 1 : modeX) - FIELD_GAP
  let creditsX = -1
  if (credits.length > 0) {
    const w = measureText(credits)
    if (w <= spanEnd - spanStart) creditsX = spanStart + ((spanEnd - spanStart - w) >> 1)
  }
  return { ballX, creditsX, modeX }
}

export function drawText(
  raster: DmdRaster,
  text: string,
  x: number,
  y: number,
  level = 15,
  contrast = false,
): void {
  let cursor = x
  for (const raw of drawable(text)) {
    const glyph = GLYPHS[raw] ?? GLYPHS['?']!
    for (let col = 0; col < GLYPH_WIDTH; col++) {
      const bits = glyph[col]!
      for (let row = 0; row < 7; row++) {
        if ((bits >> row) & 1) raster.set(cursor + col, y + row,
          contrast && raster.get(cursor + col, y + row) >= 8 ? 0 : level)
      }
    }
    cursor += GLYPH_ADVANCE
  }
}

// --- the scene player -------------------------------------------------------------------

export interface DmdScene {
  readonly id: string
  readonly kind?: string
  readonly loop?: boolean
  readonly durationMs?: number
  readonly layout?: string
  readonly frames?: readonly Record<string, unknown>[]
}

/** What a `score` scene needs. Supplied by the game, never read from it. */
export interface DmdScoreView {
  /** Scalar rule state supplied by the host; text bindings never execute expressions. */
  readonly variables?: Readonly<Record<string, number>>
  readonly scores: readonly number[]
  readonly currentPlayer: number
  readonly ball: number
  /**
   * Credits in the machine, drawn along the bottom of the score display (M8-39).
   *
   * **The coin slot has worked since M8-30 and the number has never been visible on either
   * client.** `game.credits` had exactly one reader in the whole repository — `harness/main.ts`,
   * which is a test harness and not the shipped client. Optional so a caller that has no game
   * behind it still type-checks, and zero-suppressed when it draws.
   */
  readonly credits?: number
  readonly title?: string
  /** Mode name and remaining fraction, for the draining bar. */
  readonly mode?: { readonly name: string; readonly remaining: number }

  /**
   * The three characters the machine is asking for, as they stand (M8-47).
   *
   * ***A real machine shows you the letters you are choosing.*** NEON NOVA declares
   * `"kind": "entry"` for its `highScoreEntry` scene and **no client ever implemented that
   * kind** — this player understood exactly one, `'score'`, so the scene fell through the
   * `frames === undefined` branch into `drawScoreScene` and produced a bare score. On a
   * one-player game that is pixel-identical to `scoreMain`, which is why nothing looked broken.
   *
   * **This client hid it from itself**: it draws initials entry in the HTML HUD
   * (`MachineStage.tsx`, one span per letter) and narrates it in `narration.ts`, so the glass
   * being empty cost a browser player nothing. The Quest has no HUD — the backglass is its only
   * score surface — so a player in a headset was asked for three characters and shown none.
   */
  readonly initials?: string

  /** Which of the three is being edited, or −1 when the machine is not asking. */
  readonly initialsAt?: number

  /**
   * Whose score is being signed. −1 when the machine is not asking.
   *
   * **Not `currentPlayer`.** `openHighScoreEntry` walks every player and stops at the first who
   * has not yet put initials up, so a four-player game can be asking player 3 while
   * `currentPlayer` is someone else. Indexing `scores` by the wrong one is the quiet kind of
   * wrong — a display showing a score, just not the score being signed.
   */
  readonly initialsFor?: number
}

export interface DmdRenderContext {
  /** Steps since the scene started. The scene's only notion of time. */
  readonly elapsedSteps: number
  readonly score: DmdScoreView
  /** Seeded per scene start, so a replay's starfield is the replay's starfield. */
  readonly rng: Rng
  /**
   * How *this* host says "start the ball", substituted for `{startHint}` in a text op.
   *
   * **The machine cannot know this and must not guess.** One `machine.json` is read by three
   * hosts whose plunger is three different gestures: a phone drags it down, a Quest player pulls
   * it in the air, and a desktop holds the space bar. An attract marquee that reads "PRESS
   * START" on a phone is telling a player to look for a button that is not on the screen.
   *
   * The same reasoning as `keyGuide.ts` — the wording belongs beside the input layer that owns
   * the gesture, not baked into the artwork, because a legend that drifts from the control is
   * worse than no legend at all.
   *
   * **Optional**, and that is deliberate rather than lax: only two of the eleven ops read it, so
   * requiring it would make every caller that assembles a context — including tests that drive a
   * single op — declare a field they have no opinion about. Absent means `DEFAULT_START_HINT`.
   */
  readonly startHint?: string
  /** Live-host readability; explicit authored intensity and raw artwork previews stay exact. */
  readonly contrastText?: boolean
}

/**
 * The default wording, used by any host that has not said which it is.
 *
 * Deliberately the *neutral* phrasing rather than one of the three: a host that forgets to set
 * this shows something true everywhere instead of instructing a phone player to press a key.
 */
export const DEFAULT_START_HINT = 'LAUNCH THE BALL'

/** The token an author writes in a machine's display text. */
const START_HINT_TOKEN = '{startHint}'

/**
 * Substitute the host's control wording into a text op.
 *
 * A plain replace rather than a template language: the format has exactly one thing a display
 * cannot know for itself, and a general interpolator would be a scripting surface in an asset
 * that is meant to be inert.
 */
function resolveText(text: string, ctx: DmdRenderContext): string {
  return text.split(START_HINT_TOKEN).join(ctx.startHint ?? DEFAULT_START_HINT)
    .replace(/\{var:([A-Za-z][A-Za-z0-9_]*)\}/g, (_, id: string) => String(Math.floor(ctx.score.variables?.[id] ?? 0)))
}

/**
 * Plays one machine's display program.
 *
 * Holds the current scene and the step it started on; `render` produces a raster for a given
 * step. Nothing here allocates per frame beyond the raster it was handed.
 */
export class DmdPlayer {
  readonly raster: DmdRaster
  private readonly scenes = new Map<string, DmdScene>()
  private current: DmdScene | undefined
  private startedStep = 0
  private rngSeed = 1
  /** The scene to fall back to when a timed scene finishes. */
  idleScene = 'scoreMain'
  /**
   * What this host calls the gesture that launches a ball (see `DmdRenderContext.startHint`).
   *
   * Mutable and defaulted, so a host sets it once at construction and every scene that mentions
   * `{startHint}` is right from then on. Neutral until told, never a guess.
   */
  startHint: string = DEFAULT_START_HINT
  contrastText = false

  constructor(display: MachineDisplay | undefined, width = DMD_WIDTH, height = DMD_HEIGHT) {
    this.raster = new DmdRaster(display?.width ?? width, display?.height ?? height)
    for (const scene of display?.scenes ?? []) this.scenes.set(scene.id, scene as DmdScene)
  }

  get sceneId(): string | undefined {
    return this.current?.id
  }

  has(id: string): boolean {
    return this.scenes.has(id)
  }

  /** Switch scenes. A scene that does not exist leaves the display alone rather than blanking. */
  show(id: string, step: number): boolean {
    const scene = this.scenes.get(id)
    if (scene === undefined) return false
    this.current = scene
    this.startedStep = step
    // Derived from the step so two runs of the same replay dissolve identically.
    this.rngSeed = (Math.imul(step, 2654435761) ^ hashString(id)) >>> 0
    return true
  }

  /**
   * Draw the current scene for `step`, returning the raster.
   *
   * A timed scene that has run out hands back to `idleScene` here rather than through a timer,
   * because a timer would be a clock and this display is driven by step index.
   */
  render(step: number, score: DmdScoreView): DmdRaster {
    let scene = this.current
    if (scene === undefined) {
      if (!this.show(this.idleScene, step)) return this.raster
      scene = this.current!
    }

    const elapsed = step - this.startedStep
    if (scene.durationMs !== undefined && scene.loop !== true) {
      const durationSteps = Math.round(scene.durationMs * (STEP_HZ / 1000))
      if (elapsed >= durationSteps && scene.id !== this.idleScene) {
        this.show(this.idleScene, step)
        scene = this.current!
      }
    }

    this.raster.clear(0)
    const ctx: DmdRenderContext = {
      elapsedSteps: step - this.startedStep,
      score,
      // A fresh stream every frame, seeded from the scene rather than carried: an op that
      // draws a starfield has to place the *same* stars on every frame of the scene, so the
      // sequence must restart, not continue.
      rng: new Rng(this.rngSeed),
      startHint: this.startHint,
      contrastText: this.contrastText,
    }
    // **A scene kind the machines declare and nothing drew** (M8-47). Tested before the
    // `frames === undefined` fall-through below, which is precisely what swallowed it: a scene
    // carrying a `kind` this player has never heard of and no `frames` is indistinguishable there
    // from a scene that asked for the score.
    //
    // Falls through when the machine is not actually asking, rather than drawing an empty entry
    // screen: a definition naming `entry` as its idle scene is an authoring mistake, and
    // `request` already prefers "carry on" to "blank" for that class.
    if (scene.kind === 'entry' && (ctx.score.initialsAt ?? -1) >= 0) {
      drawEntryScene(this.raster, ctx)
      return this.raster
    }
    if (scene.kind === 'score' || scene.frames === undefined) {
      drawScoreScene(this.raster, scene, ctx)
      return this.raster
    }
    for (const op of scene.frames) runDisplayOp(this.raster, op, ctx)
    return this.raster
  }
}

function hashString(s: string): number {
  let h = 0x811c9dc5
  for (let i = 0; i < s.length; i++) h = Math.imul(h ^ s.charCodeAt(i), 0x01000193) >>> 0
  return h >>> 0
}

/** Group a score with thousands separators, the way the display has always shown it. */
export function formatScore(score: number): string {
  const digits = Math.max(0, Math.floor(score)).toString()
  let out = ''
  for (let i = 0; i < digits.length; i++) {
    if (i > 0 && (digits.length - i) % 3 === 0) out += ','
    out += digits[i]
  }
  return out
}

/**
 * The three characters, the score being signed, and which letter the flippers are on.
 *
 * Laid out from the raster's own dimensions rather than from NEON NOVA's 128x32, because a machine
 * declares its own `display.width`. The letter pitch steps down instead of overflowing, and the
 * prompt is dropped before the letters are — a player who cannot read the letters cannot enter
 * initials at all, while one who cannot read the prompt has only lost the caption on something
 * they can still see.
 */
function drawEntryScene(raster: DmdRaster, ctx: DmdRenderContext): void {
  const text = ctx.score.initials ?? ''
  if (text.length === 0) return

  // The widest pitch that fits, so the letters read as three wheels rather than a word.
  let pitch = GLYPH_ADVANCE * 3
  while (pitch > GLYPH_ADVANCE && (text.length - 1) * pitch + GLYPH_WIDTH > raster.width - 2) {
    pitch -= GLYPH_ADVANCE
  }
  const left = Math.max(0, (raster.width - ((text.length - 1) * pitch + GLYPH_WIDTH)) >> 1)

  // The score being signed, above the letters. `initialsFor`, not `currentPlayer`.
  const scores = ctx.score.scores
  const forWhom = ctx.score.initialsFor ?? -1
  const who = forWhom >= 0 && forWhom < scores.length ? forWhom : ctx.score.currentPlayer
  if (who >= 0 && who < scores.length) {
    const value = formatScore(scores[who] ?? 0)
    drawText(raster, value, Math.max(0, (raster.width - measureText(value)) >> 1), 1, 12)
  }

  const at = ctx.score.initialsAt ?? -1
  const row = raster.height >= 32 ? 12 : Math.max(0, raster.height - GLYPH_HEIGHT - 1)
  for (let i = 0; i < text.length; i++) {
    // Bright for the one the flippers are turning, legible but plainly unselected for the rest.
    // A player has to see all three and know which one moves.
    drawText(raster, text[i]!, left + i * pitch, row, i === at ? 15 : 8)
  }

  if (at >= 0 && at < text.length && Math.floor(ctx.elapsedSteps / BLINK_STEPS) % 2 === 0) {
    const y = row + GLYPH_HEIGHT
    for (let x = 0; x < GLYPH_WIDTH; x++) raster.set(left + at * pitch + x, y, 15)
  }

  const promptRow = raster.height - GLYPH_HEIGHT - 1
  if (promptRow >= row + GLYPH_HEIGHT + 2) {
    drawText(
      raster,
      ENTRY_PROMPT,
      Math.max(0, (raster.width - measureText(ENTRY_PROMPT)) >> 1),
      promptRow,
      7,
    )
  }
}

function drawScoreScene(raster: DmdRaster, scene: DmdScene, ctx: DmdRenderContext): void {
  const { scores, currentPlayer, ball } = ctx.score
  const players = scene.layout === 'fourPlayer' ? Math.min(4, scores.length) : 1

  if (players <= 1) {
    const text = formatScore(scores[currentPlayer] ?? 0)
    drawText(raster, text, Math.max(0, (raster.width - measureText(text)) >> 1), 6, 15)
  } else {
    // Two columns of two, the classic four-player layout.
    for (let i = 0; i < players; i++) {
      const text = formatScore(scores[i] ?? 0)
      const col = i % 2
      const row = i >> 1
      const x = col === 0 ? 1 : raster.width - measureText(text) - 1
      drawText(raster, text, x, row * 9 + 1, i === currentPlayer ? 15 : 7)
    }
  }

  // **One layout for the three fields, asked whether they fit** (M8-67). See `bottomRow` for the
  // row this used to draw, which had one dark column between the ball and the credits and none at
  // all once a mode name arrived. The credits are dim rather than bright: standing information,
  // not an event, and the eye is on the score.
  const footer = `BALL ${ball}`
  const credits = ctx.score.credits ?? 0
  const line = credits > 0 ? `CREDITS ${credits}` : ''
  const mode = ctx.score.mode
  const label = mode === undefined ? '' : mode.name.slice(0, 10)
  const row = bottomRow(raster.width, footer, line, label)
  drawText(raster, footer, row.ballX, raster.height - 8, 9)
  if (row.creditsX >= 0) drawText(raster, line, row.creditsX, raster.height - 8, 7)
  if (mode !== undefined) {
    drawText(raster, label, row.modeX, raster.height - 8, 12)
    // A draining bar reads faster than a number, which is why every machine of the era used one.
    const barWidth = Math.round(mode.remaining * (raster.width - 2))
    for (let x = 1; x <= barWidth; x++) raster.set(x, raster.height - 1, 11)
  }
}

// --- the generator ops -------------------------------------------------------------------

type OpHandler = (raster: DmdRaster, op: Record<string, unknown>, ctx: DmdRenderContext) => void

const OPS: Record<DisplayOp, OpHandler> = {
  text: opText,
  scrollText: opScrollText,
  rect: opRect,
  line: opLine,
  circle: opCircle,
  starfield: opStarfield,
  plasma: opPlasma,
  wipe: opWipe,
  dissolve: opDissolve,
  invert: opInvert,
  scoreField: opScoreField,
}

export function runDisplayOp(
  raster: DmdRaster,
  op: Record<string, unknown>,
  ctx: DmdRenderContext,
): boolean {
  const name = op['op']
  if (typeof name !== 'string' || !(DISPLAY_OPS as readonly string[]).includes(name)) return false
  OPS[name as DisplayOp](raster, op, ctx)
  return true
}

function n(v: unknown, fallback: number): number {
  return typeof v === 'number' && Number.isFinite(v) ? v : fallback
}
function t(v: unknown, fallback: string): string {
  return typeof v === 'string' ? v : fallback
}

function opText(raster: DmdRaster, op: Record<string, unknown>, ctx: DmdRenderContext): void {
  const values = typeof op['values']==='string' && op['values'].length>0 ? op['values'].split('|').slice(0,64) : undefined, binding = op['var']
  const index = typeof binding === 'string' && binding.length>0 && ctx.score.variables?.[binding]!==undefined ? Math.floor(ctx.score.variables[binding]!) : -1
  const selected = Array.isArray(values) && index >= 0 && index < Math.min(64, values.length) ? values[index] : op['text']
  const text = resolveText(t(selected, ''), ctx)
  const x = op['x'] === undefined ? (raster.width - measureText(text)) >> 1 : n(op['x'], 0)
  drawText(raster, text, x, n(op['y'], 12), n(op['level'], 15), ctx.contrastText === true && op['level'] === undefined)
}

function opScrollText(raster: DmdRaster, op: Record<string, unknown>, ctx: DmdRenderContext): void {
  const text = resolveText(t(op['text'], ''), ctx)
  const speed = n(op['speed'], 22) // dots per second
  const width = measureText(text)
  const travel = width + raster.width
  // Modulo on the step index, so the marquee is at the same place on the same step for ever.
  const offset = travel === 0 ? 0 : ((ctx.elapsedSteps * speed) / STEP_HZ) % travel
  drawText(raster, text, Math.round(raster.width - offset), n(op['y'], 12), n(op['level'], 13))
}

function opRect(raster: DmdRaster, op: Record<string, unknown>, _ctx: DmdRenderContext): void {
  const min = Array.isArray(op['min']) ? (op['min'] as number[]) : [0, 0]
  const max = Array.isArray(op['max']) ? (op['max'] as number[]) : [raster.width - 1, raster.height - 1]
  const level = n(op['level'], 15)
  const filled = op['fill'] === true
  for (let y = min[1]!; y <= max[1]!; y++) {
    for (let x = min[0]!; x <= max[0]!; x++) {
      if (filled || y === min[1] || y === max[1] || x === min[0] || x === max[0]) {
        raster.set(x, y, level)
      }
    }
  }
}

function opLine(raster: DmdRaster, op: Record<string, unknown>, _ctx: DmdRenderContext): void {
  const from = Array.isArray(op['from']) ? (op['from'] as number[]) : [0, 0]
  const to = Array.isArray(op['to']) ? (op['to'] as number[]) : [raster.width - 1, 0]
  const level = n(op['level'], 15)
  // Bresenham. Integers only, so the same line appears on every runtime.
  let x0 = Math.round(from[0]!)
  let y0 = Math.round(from[1]!)
  const x1 = Math.round(to[0]!)
  const y1 = Math.round(to[1]!)
  const dx = Math.abs(x1 - x0)
  const dy = -Math.abs(y1 - y0)
  const sx = x0 < x1 ? 1 : -1
  const sy = y0 < y1 ? 1 : -1
  let err = dx + dy
  for (let guard = 0; guard < 4096; guard++) {
    raster.set(x0, y0, level)
    if (x0 === x1 && y0 === y1) break
    const e2 = 2 * err
    if (e2 >= dy) {
      err += dy
      x0 += sx
    }
    if (e2 <= dx) {
      err += dx
      y0 += sy
    }
  }
}

function opCircle(raster: DmdRaster, op: Record<string, unknown>, _ctx: DmdRenderContext): void {
  const centre = Array.isArray(op['centre']) ? (op['centre'] as number[]) : [raster.width / 2, raster.height / 2]
  const r = n(op['radius'], 8)
  const level = n(op['level'], 15)
  const filled = op['fill'] === true
  const cx = centre[0]!
  const cy = centre[1]!
  for (let y = Math.floor(cy - r); y <= Math.ceil(cy + r); y++) {
    for (let x = Math.floor(cx - r); x <= Math.ceil(cx + r); x++) {
      const d = Math.sqrt((x - cx) * (x - cx) + (y - cy) * (y - cy))
      if (filled ? d <= r : Math.abs(d - r) <= 0.7) raster.set(x, y, level)
    }
  }
}

function opStarfield(raster: DmdRaster, op: Record<string, unknown>, ctx: DmdRenderContext): void {
  const count = Math.min(1200, Math.max(0, Math.floor(n(op['count'], 60))))
  const drift = Array.isArray(op['drift']) ? (op['drift'] as number[]) : [0.4, 0]
  const seconds = ctx.elapsedSteps / STEP_HZ
  for (let i = 0; i < count; i++) {
    const x0 = ctx.rng.nextFloat() * raster.width
    const y0 = ctx.rng.nextFloat() * raster.height
    const depth = 0.35 + ctx.rng.nextFloat() * 0.65
    const x = mod(x0 + drift[0]! * seconds * depth * 12, raster.width)
    const y = mod(y0 + drift[1]! * seconds * depth * 12, raster.height)
    raster.set(Math.floor(x), Math.floor(y), Math.round(4 + depth * 11))
  }
}

function mod(v: number, m: number): number {
  const r = v % m
  return r < 0 ? r + m : r
}

/**
 * Interference of three moving sinusoids — the classic demoscene plasma, and the one effect
 * that makes a 4-bit display look alive.
 */
function opPlasma(raster: DmdRaster, op: Record<string, unknown>, ctx: DmdRenderContext): void {
  const scale = n(op['scale'], 0.5)
  const speed = n(op['speed'], 0.4)
  const seconds = ctx.elapsedSteps / STEP_HZ
  const k = 0.25 * scale
  for (let y = 0; y < raster.height; y++) {
    for (let x = 0; x < raster.width; x++) {
      const v =
        Math.sin(x * k + seconds * speed * 3) +
        Math.sin(y * k * 1.7 - seconds * speed * 2) +
        Math.sin((x + y) * k * 0.6 + seconds * speed * 1.3)
      raster.set(x, y, Math.round(((v + 3) / 6) * 15))
    }
  }
}

/** A block of text revealed by a moving edge. `direction` is where the reveal comes from. */
function opWipe(raster: DmdRaster, op: Record<string, unknown>, ctx: DmdRenderContext): void {
  const text = t(op['text'], '')
  const durationSteps = Math.round(n(op['durationMs'], 900) * (STEP_HZ / 1000))
  const progress = durationSteps <= 0 ? 1 : Math.min(1, ctx.elapsedSteps / durationSteps)
  const scratch = new DmdRaster(raster.width, raster.height)
  drawText(scratch, text, (raster.width - measureText(text)) >> 1, (raster.height - 7) >> 1, 15)

  const direction = t(op['direction'], 'left')
  for (let y = 0; y < raster.height; y++) {
    for (let x = 0; x < raster.width; x++) {
      const along =
        direction === 'left' ? x / raster.width
        : direction === 'right' ? 1 - x / raster.width
        : direction === 'up' ? y / raster.height
        : 1 - y / raster.height
      if (along <= progress) raster.set(x, y, scratch.get(x, y))
    }
  }
}

/** Dots arrive in a fixed pseudo-random order — the same order on every runtime. */
function opDissolve(raster: DmdRaster, op: Record<string, unknown>, ctx: DmdRenderContext): void {
  const steps = Math.max(1, Math.floor(n(op['steps'], 12)))
  const durationSteps = Math.round(n(op['durationMs'], 700) * (STEP_HZ / 1000))
  const progress = durationSteps <= 0 ? 1 : Math.min(1, ctx.elapsedSteps / durationSteps)
  const stage = Math.floor(progress * steps)
  const level = n(op['level'], 15)
  for (let y = 0; y < raster.height; y++) {
    for (let x = 0; x < raster.width; x++) {
      if (hashString(`${x},${y}`) % steps <= stage) raster.set(x, y, level)
    }
  }
}

function opInvert(raster: DmdRaster, _op: Record<string, unknown>, _ctx: DmdRenderContext): void {
  for (let i = 0; i < raster.dots.length; i++) raster.dots[i] = 15 - raster.dots[i]!
}

function opScoreField(raster: DmdRaster, op: Record<string, unknown>, ctx: DmdRenderContext): void {
  const player = Math.max(0, Math.floor(n(op['player'], ctx.score.currentPlayer)))
  const text = formatScore(ctx.score.scores[player] ?? 0)
  const x = op['x'] === undefined ? (raster.width - measureText(text)) >> 1 : n(op['x'], 0)
  drawText(raster, text, x, n(op['y'], 6), n(op['level'], 15))
}

/** Every op the player implements. A test asserts this is exactly `DISPLAY_OPS`. */
export const IMPLEMENTED_DISPLAY_OPS: readonly string[] = Object.keys(OPS)
